import axios, { AxiosError } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { DEV_AUTH_BYPASS } from '../auth/devAuth';
import { reportApiAuthRejected } from '../auth/authState';
import { msalInstance, apiScopes, loginRequest } from '../auth/msalConfig';
import type { ApiErrorBody } from './types';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
});

api.interceptors.request.use(async (config) => {
  if (DEV_AUTH_BYPASS) return config;

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return config;

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    config.headers.set('Authorization', `Bearer ${result.accessToken}`);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(loginRequest);
    }
    throw error;
  }
  return config;
});

/** The single error envelope the API returns, unwrapped for the UI. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: { field: string; message: string }[] | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-keyed messages, for wiring server validation back onto a form. */
  get fieldErrors(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const d of this.details ?? []) if (!map[d.field]) map[d.field] = d.message;
    return map;
  }
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: ApiErrorBody }>) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data?.error;

    // A 401 here means MSAL gave us a token the API would not accept — a blocked sign-in domain
    // being the expected cause. Surface it once so the user gets a sign-in screen rather than a
    // generic error on every page.
    if ((status === 401 || status === 403) && !DEV_AUTH_BYPASS) reportApiAuthRejected();
    if (body) {
      return Promise.reject(new ApiError(body.code, body.message, status, body.details));
    }
    return Promise.reject(
      new ApiError(
        status === 0 ? 'network_error' : 'server_error',
        status === 0 ? 'Could not reach the server. Check your connection.' : error.message,
        status,
      ),
    );
  },
);
